import { ReactNode, useMemo } from 'react';

import { AvatarDisplay } from '@/components/AvatarDisplay';
import { Badge } from '@/components/Badge';
import { Table, TableColumn } from '@/components/Table';

import type { IAdminUser } from '@/types/user';

import { formatCurrency, formatRelativeDate } from '@/utils/formatting';
import { capitalizeFirstLetter } from '@/utils/stringUtils';

interface UserManagementTableProps {
  users: IAdminUser[];
  onUserClick?: (user: IAdminUser) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  loading?: boolean;
  emptyMessage?: ReactNode;
}

export function UserManagementTable({
  users,
  onUserClick,
  sortBy,
  sortOrder,
  onSort,
  loading = false,
  emptyMessage,
}: UserManagementTableProps) {
  const columns: TableColumn<IAdminUser>[] = useMemo(
    () => [
      {
        header: (
          <button
            onClick={() =>
              onSort?.('name', sortBy === 'name' && sortOrder === 'asc' ? 'desc' : 'asc')
            }
            className="flex items-center gap-1 hover:text-gray-700"
          >
            X-TEAMER NAME
            <span className="text-gray-400">
              {sortBy === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
            </span>
          </button>
        ),
        accessor: (user) => (
          <div className="flex items-center">
            <AvatarDisplay name={user.name} picture={user.picture} size="md" />
            <button
              onClick={() => onUserClick?.(user)}
              className="ml-3 text-sm font-medium text-gray-900 hover:text-red-600"
            >
              {user.name}
            </button>
          </div>
        ),
      },
      {
        header: 'STATUS',
        accessor: (user) => <Badge variant="status">{capitalizeFirstLetter(user.status)}</Badge>,
      },
      {
        header: 'USER TYPE',
        accessor: (user) => <Badge variant="role">{capitalizeFirstLetter(user.role)}</Badge>,
      },
      {
        header: 'EMPLOYMENT',
        accessor: (user) => (
          <Badge variant="employmentType">{capitalizeFirstLetter(user.employmentType)}</Badge>
        ),
      },
      {
        header: 'COUNTRY',
        accessor: (user) => <span className="text-sm text-gray-900">{user.country}</span>,
      },
      {
        header: 'BALANCE',
        accessor: (user) => (
          <span className="text-sm font-medium text-gray-900">
            {formatCurrency(user.unleashBalance, true)}
          </span>
        ),
      },
      {
        header: 'CREATED DATE',
        accessor: (user) => (
          <span className="text-sm text-gray-500">{formatRelativeDate(user.createdDate)}</span>
        ),
      },
      {
        header: 'LAST CLAIM DATE',
        accessor: (user) => (
          <span className="text-sm text-gray-500">
            {user.lastClaimDate ? formatRelativeDate(user.lastClaimDate) : '—'}
          </span>
        ),
      },
    ],
    [onUserClick, onSort, sortBy, sortOrder],
  );

  return (
    <Table
      columns={columns}
      data={users}
      loading={loading}
      skeletonRows={25}
      emptyMessage={emptyMessage}
      getRowKey={(user) => user.id}
    />
  );
}
